/**
 * LESection - Reusable section wrapper for Loan Estimate viewer
 *
 * Provides consistent styling for each section with optional icon and collapsible behavior
 */

import React from 'react';
import { cn } from '@/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';

interface LESectionProps {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  rightContent?: React.ReactNode;
}

export const LESection: React.FC<LESectionProps> = ({
  title,
  icon,
  children,
  className,
  collapsible = false,
  defaultOpen = true,
  rightContent
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const headerContent = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        <span className="font-semibold text-sm">{title}</span>
      </div>
      <div className="flex items-center gap-2">
        {rightContent}
        {collapsible && (
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
          />
        )}
      </div>
    </div>
  );

  if (collapsible) {
    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className={cn('rounded-lg border bg-card', className)}>
          <CollapsibleTrigger className="w-full px-4 py-3 hover:bg-muted/50 transition-colors rounded-t-lg">
            {headerContent}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-4 pb-4 pt-2">{children}</div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    );
  }

  return (
    <div className={cn('rounded-lg border bg-card', className)}>
      <div className="px-4 py-3 border-b bg-muted/30 rounded-t-lg">
        {headerContent}
      </div>
      <div className="px-4 py-4">{children}</div>
    </div>
  );
};

/**
 * Section Divider - horizontal line between major sections
 */
export const LESectionDivider: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn('border-t border-dashed my-3', className)} />
);

/**
 * Subsection Header - smaller header within a section
 */
export const LESubsectionHeader: React.FC<{
  title: string;
  amount?: number;
  className?: string;
}> = ({ title, amount, className }) => (
  <div
    className={cn(
      'flex items-center justify-between py-2 text-sm font-medium text-muted-foreground',
      className
    )}
  >
    <span>{title}</span>
    {amount !== undefined && (
      <span className="font-semibold text-foreground">
        {formatCurrency(amount)}
      </span>
    )}
  </div>
);

// Currency formatter
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

export default LESection;
